import React, { useState, useCallback, useEffect, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import UserService from "../../../services/UserService";
import Loading from "../../../hooks/Loading";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import countries from "../../../utils/countries";
import provincesCanada from "../../../utils/provincesCanada";
import citiesCanada from "../../../utils/citiesCanada";
import roles from "../../../utils/roles";

const AutocompleteInput = ({
                               name,
                               value,
                               options,
                               onChange,
                               placeholder,
                               label,
                               error,
                               touched,
                               disabled = false,
                               allowCustomInput = false,
                           }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [inputValue, setInputValue] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const containerRef = useRef(null);
    const suggestionsRef = useRef(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        if (disabled) return;
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowSuggestions(true);

        // Mettre à jour immédiatement la valeur si allowCustomInput est true
        if (allowCustomInput) {
            onChange({
                target: {
                    name,
                    value: newValue,
                },
            });
        }

        // Filtrer les suggestions basées sur la saisie
        const filteredSuggestions = options.filter((option) =>
            option.toLowerCase().includes(newValue.toLowerCase())
        );
        setSuggestions(filteredSuggestions);
    };

    const handleSuggestionClick = (suggestion) => {
        if (disabled) return;

        setInputValue(suggestion);
        onChange({
            target: {
                name,
                value: suggestion,
            },
        });
        setShowSuggestions(false);
    };

    const handleBlur = () => {
        if (disabled) return;

        // Si allowCustomInput est true, ne pas valider contre les options
        if (allowCustomInput) {
            return;
        }

        // Vérifier si la valeur saisie est dans les options
        const isValidOption = options.some(
            (option) => option.toLowerCase() === inputValue.toLowerCase()
        );

        if (!isValidOption) {
            onChange({
                target: {
                    name,
                    value: "",
                },
            });
        }
    };

    const handleFocus = () => {
        if (disabled) return;

        if (inputValue) {
            const filteredSuggestions = options.filter((option) =>
                option.toLowerCase().includes(inputValue.toLowerCase())
            );
            setSuggestions(filteredSuggestions);
            setShowSuggestions(true);
        }
    };

    return (
        <div
            className="form-floating mt-24 position-relative"
            ref={containerRef}
            style={{ zIndex: showSuggestions ? 1000 : 1 }}
        >
            <input
                type="text"
                name={name}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                disabled={disabled}
                className={`form-control ${
                    touched ? (error ? "is-invalid" : "is-valid") : ""
                }`}
                placeholder={placeholder}
            />
            <label>{label}</label>

            {showSuggestions && suggestions.length > 0 && (
                <div
                    ref={suggestionsRef}
                    className="suggestions-list position-absolute border rounded"
                    style={{
                        top: "90%",
                        left: 0,
                        width: "100%",
                        maxHeight: "200px",
                        overflowY: "auto",
                        boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
                        zIndex: 100000,
                    }}
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="suggestion-item p-2 hover:bg-light"
                            style={{ cursor: "pointer" }}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}

            {touched && error && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </div>
    );
};

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirthString: '',
        profession: '',
        streetAddress: '',
        neighborhood: '',
        country: 'Canada',
        city: '',
        province: '',
        username: '',
        password: '',
        confirmPassword: '',
        additionalInfos: '',
        role: roles[0]
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const validatePassword = useCallback((password) => {
        if (!password) return 'Le mot de passe est requis';
        if (password.length < 5) return 'Le mot de passe doit contenir au moins 5 caractères';
        return '';
    }, []);

    const validateField = useCallback((name, value) => {
        switch (name) {
            case 'firstName':
                return !value ? 'Le prénom est requis' : '';
            case 'lastName':
                return !value ? 'Le nom est requis' : '';
            case 'email':
                return !value ? 'L\'email est requis' :
                    !/\S+@\S+\.\S+/.test(value) ? 'Format d\'email invalide' : '';
            case 'phoneNumber':
                return !value || value.length < 10 ? 'Le numéro de téléphone est requis et doit être valide' : '';
            case 'dateOfBirthString':
                return !value ? 'La date de naissance est requise' : '';
            case 'username':
                return !value ? 'Le nom d\'utilisateur est requis' :
                    value.length < 4 ? 'Le nom d\'utilisateur doit contenir au moins 4 caractères' : '';
            case 'password':
                return validatePassword(value);
            case 'confirmPassword':
                return !value ? 'La confirmation du mot de passe est requise' :
                    value !== formData.password ? 'Les mots de passe ne correspondent pas' : '';
            case 'streetAddress':
                return !value ? 'L\'adresse est requise' : '';
            case 'city':
                return !value ? 'La ville est requise' : '';
            case 'province':
                return !value ? 'La province est requise' : '';
            case 'country':
                return !value ? 'Le pays est requis' : '';
            case 'role':
                return !value ? 'Le rôle est requis' : '';
            default:
                return '';
        }
    }, [formData.password, validatePassword]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        if (name === 'province') {
            setFormData(prev => ({
                ...prev,
                city: ''
            }));
        }

        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    }, [validateField]);

    const handlePhoneChange = useCallback((value) => {
        setFormData(prev => ({
            ...prev,
            phoneNumber: value
        }));

        setTouched(prev => ({
            ...prev,
            phoneNumber: true
        }));

        const error = validateField('phoneNumber', value);
        setErrors(prev => ({
            ...prev,
            phoneNumber: error
        }));
    }, [validateField]);

    const isStepValid = useCallback((step) => {
        const fieldsToValidate = {
            0: ['firstName', 'lastName', 'email', 'phoneNumber', 'dateOfBirthString'],
            1: ['country', 'streetAddress', 'province', 'city'],
            2: ['username', 'password', 'confirmPassword', 'role']
        };

        return fieldsToValidate[step].every(field => {
            const error = validateField(field, formData[field]);
            return !error && touched[field];
        });
    }, [formData, touched, validateField]);

    const handleNext = useCallback(() => {
        if (isStepValid(currentStep)) {
            setCurrentStep(prev => prev + 1);
        }
    }, [currentStep, isStepValid]);

    const handlePrevious = useCallback(() => {
        setCurrentStep(prev => prev - 1);
    }, []);

    const prepareFormData = (data) => {
        const { confirmPassword, role, ...rest } = data;
        return {
            ...rest,
            roleNames: [role]
        };
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!isStepValid(2)) return;

        setIsSubmitting(true);
        setIsLoading(true);
        try {
            const preparedData = prepareFormData(formData);
            await UserService.registerUser(preparedData);
            toast.success('Utilisateur créé avec succès !');
            setIsSubmitting(false);
            setIsLoading(false);
            onUserCreated(); // Fermer la modal
            navigate('/users'); // Recharger la page /users
            return true;
        } catch (error) {
            toast.error('La création de l\'utilisateur a échoué. Veuillez réessayer.');
            console.error(error);
            setIsSubmitting(false);
            setIsLoading(false);
            return false;
        }
    }, [formData, isStepValid, onUserCreated, navigate]);

    // Fonction pour valider le pays par défaut (Canada)
    useEffect(() => {
        if (currentStep === 1 && formData.country === "Canada") {
            handleInputChange({
                target: {
                    name: "country",
                    value: "Canada",
                },
            });
        }
    }, [formData.country, currentStep]);

    // Validation de l'âge
    const validateAge = (dateOfBirthString) => {
        const today = new Date();
        const birthDate = new Date(dateOfBirthString);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= 18;
        }
        return age >= 18;
    };

    return (
        <div className={`modal ${isOpen ? "d-block" : "d-none"}`}>
            {isLoading && <Loading />}

            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Créer un utilisateur</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <form
                            id="msform"
                            onSubmit={handleSubmit}
                        >
                            <div className="progress">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${(currentStep + 1) * 33.33}%`,
                                    }}
                                />
                            </div>

                            {/* Étape 1: Informations Personnelles */}
                            <fieldset style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                                <div className="personal-info">
                                    <div className="home-address-heading">
                                        <h2>Informations Personnelles</h2>
                                    </div>
                                    <div className="multistep-form">
                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.firstName ? (errors.firstName ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Prénom"
                                            />
                                            <label>Prénom</label>
                                            {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                        </div>

                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.lastName ? (errors.lastName ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Nom"
                                            />
                                            <label>Nom</label>
                                            {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                        </div>

                                        <div className="form-floating mt-24 mb-5">
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.email ? (errors.email ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Email"
                                            />
                                            <label>Email</label>
                                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                        </div>

                                        <div className="form-floating mt-24">
                                            <label className="mb-5" style={{ top: '-42px' }}>Numéro de téléphone</label>
                                            <PhoneInput
                                                country={'ca'}
                                                value={formData.phoneNumber}
                                                onChange={handlePhoneChange}
                                                inputClass={`form-control ${touched.phoneNumber ? (errors.phoneNumber ? 'is-invalid' : 'is-valid') : ''}`}
                                            />

                                            {errors.phoneNumber &&
                                                <div className="invalid-feedback">{errors.phoneNumber}</div>}
                                        </div>

                                        <div className="form-floating mt-24">
                                            <input
                                                type="date"
                                                name="dateOfBirthString"
                                                value={formData.dateOfBirthString}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.dateOfBirthString ? (errors.dateOfBirthString ? 'is-invalid' : 'is-valid') : ''}`}
                                                min="1900-01-01"
                                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                            />
                                            <label>Date de naissance</label>
                                            {errors.dateOfBirthString &&
                                                <div className="invalid-feedback">{errors.dateOfBirthString}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="justify-content-start mt-4"
                                    style={{
                                        justifyContent: "flex-start",
                                    }}
                                >
                                    {isStepValid(0) && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleNext}
                                        >
                                            Suivant
                                        </button>
                                    )}
                                </div>
                            </fieldset>

                            {/* Étape 2: Informations d'Adresse */}
                            <fieldset style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                                <div className="address-info">
                                    <div className="home-address-heading">
                                        <h2>Informations Additionnelles</h2>
                                    </div>
                                    <div className="multistep-form">
                                        <AutocompleteInput
                                            name="country"
                                            value={formData.country || "Canada"}
                                            options={countries}
                                            onChange={handleInputChange}
                                            placeholder="Pays"
                                            label="Pays"
                                            error={errors.country}
                                            touched={touched.country}
                                            allowCustomInput={true}
                                        />

                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="streetAddress"
                                                value={formData.streetAddress}
                                                onChange={handleInputChange}
                                                className={`form-control ${
                                                    touched.streetAddress
                                                        ? (errors.streetAddress
                                                            ? 'is-invalid'
                                                            : 'is-valid')
                                                        : ''
                                                }`}
                                                placeholder="Adresse"
                                            />
                                            <label>Adresse</label>
                                            {touched.streetAddress && errors.streetAddress && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.streetAddress}
                                                </div>
                                            )}
                                        </div>

                                        {/* Champ Province avec Autocomplete */}
                                        <AutocompleteInput
                                            name="province"
                                            value={formData.province}
                                            options={provincesCanada}
                                            onChange={handleInputChange}
                                            placeholder="Province"
                                            label="Province"
                                            error={errors.province}
                                            touched={touched.province}
                                        />

                                        {/* Champ Ville avec Autocomplete */}
                                        <AutocompleteInput
                                            name="city"
                                            value={formData.city}
                                            options={formData.province ? citiesCanada[formData.province] || [] : []}
                                            onChange={handleInputChange}
                                            placeholder="Ville"
                                            label="Ville"
                                            error={errors.city}
                                            touched={touched.city}
                                            disabled={!formData.province || errors.province}
                                        />

                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="profession"
                                                value={formData.profession}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="Profession"
                                            />
                                            <label>Profession</label>
                                        </div>

                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="additionalInfos"
                                                value={formData.additionalInfos}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                placeholder="Informations Additionnelles"
                                            />
                                            <label>Informations Additionnelles</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mt-4">
                                    <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                                        Précédent
                                    </button>
                                    {isStepValid(1) && (
                                        <button type="button" className="btn btn-primary" onClick={handleNext}>
                                            Suivant
                                        </button>
                                    )}
                                </div>
                            </fieldset>

                            {/* Étape 3: Informations de Connexion */}
                            <fieldset style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                                <div className="login-info">
                                    <div className="home-address-heading">
                                        <h2>Informations de Connexion</h2>
                                    </div>
                                    <div className="multistep-form">
                                        <div className="form-floating mt-24">
                                            <input
                                                type="text"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.username ? (errors.username ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Nom d'utilisateur"
                                            />
                                            <label>Nom d'utilisateur</label>
                                            {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                                        </div>

                                        <div className="form-floating mt-24 position-relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.password ? (errors.password ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Mot de passe"
                                            />
                                            <label>Mot de passe</label>
                                            <button
                                                type="button"
                                                className="btn btn-link position-absolute end-0  translate-middle-y"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{
                                                    zIndex: 5,
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: '0.375rem 0.75rem',
                                                    top: '40%',
                                                    paddingRight: '30px',
                                                    fontSize: '35px'
                                                }}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                                        </div>

                                        <div className="form-floating mt-24 position-relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`form-control ${touched.confirmPassword ? (errors.confirmPassword ? 'is-invalid' : 'is-valid') : ''}`}
                                                placeholder="Confirmer le mot de passe"
                                            />
                                            <label>Confirmer le mot de passe</label>
                                            <button
                                                type="button"
                                                className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{
                                                    zIndex: 5,
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: '0.375rem 0.75rem',
                                                    top: '40%',
                                                    paddingRight: '30px',
                                                    fontSize: '35px'
                                                }}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                            {errors.confirmPassword &&
                                                <div className="invalid-feedback">{errors.confirmPassword}</div>}
                                        </div>

                                        {/* Champ Rôle */}
                                        <div className="form-floating mt-24">
                                            <select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                className="form-control"
                                            >
                                                {roles.map((role, index) => (
                                                    <option key={index} value={role}>{role}</option>
                                                ))}
                                            </select>
                                            <label>Rôle</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mt-4">
                                    {!isSubmitting && (
                                        <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                                            Précédent
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn btn-success"
                                        disabled={!isStepValid(2) || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"
                                                      aria-hidden="true"></span>
                                                Création en cours...
                                            </>
                                        ) : (
                                            "Créer l'utilisateur"
                                        )}
                                    </button>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateUserModal;